import type { Region } from '@/shared/utils/regions';
import type { Iso8601, Uuid } from '@/shared/types/api/common';
import type { ParcelStatus } from '@/shared/ui/chips/StatusChip';
import type { ApiParcelsProductsGetResponse } from '@/shared/types/api/parcels-products';

export type { ParcelStatus };

export const PARCEL_STATUS_VALUES = [
  'DRAFT',
  'PENDING',
  'APPROVED',
  'REJECTED',
] as const satisfies readonly ParcelStatus[];

// Workflow status the parcel chip renders. Lifecycle:
//  - IN_PROGRESS — created, user hasn't pressed Confirm shipping
//  - SORTING — user confirmed, awaiting attach to a Party
//  - any PartyStatus code — parcel inherited its Party's status
export const FULFILLMENT_STATUS_VALUES = [
  'IN_PROGRESS',
  'SORTING',
  'COLLECTING',
  'SHIPPING',
  'ON_THE_WAY',
  'ON_CUSTOMS',
  'LOCAL_ON_THE_WAY',
  'SHIPMENT_ON_CUSTOMERS',
] as const;

export type FulfillmentStatus = (typeof FULFILLMENT_STATUS_VALUES)[number];

// Owner rule (#56, 2026-05-31): nested Cargo ID summary attached to every
// parcel response so the admin parcel-detail page can render a "Cargo ID"
// section without a second fetch.
export type ApiParcelCargoIdDetail = {
  id: Uuid;
  code: string | null;
  full_name: string;
  phone_number: string;
};

export type ApiParcelsGetItem = {
  id: Uuid;
  name: string;
  trek_id: string;
  cargo_id_code: string | null;
  cargo_id_detail?: ApiParcelCargoIdDetail | null;
  store_name?: string | null;
  address_name: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  street: string;
  building: string;
  apartment?: string | null;
  region: Region;
  city: string;
  phone_number: string;
  passport_series: string;
  pinfl: string;
  additional_info?: string | null;
  status: ParcelStatus;
  fulfillment_status?: FulfillmentStatus | null;
  // Owner spec (2026-05-31): True iff the physical box is currently inside
  // the JP warehouse (workflow in IN_PROGRESS/SORTING/COLLECTING). Used by
  // the Omborxona "Egasi bor posilkalar" tab to filter and by the client
  // app to gate the confirm-shipping button.
  is_in_warehouse?: boolean;
  // Bug #78 (2026-06-05): derived read-only flag — true once the parcel's
  // workflow has advanced out of IN_PROGRESS into SORTING (i.e. shipping was
  // confirmed). The edit form's "Joʻnatishni tasdiqlash" checkbox seeds from
  // this so it reflects the real stored state.
  confirmed_for_shipping?: boolean;
  reject_reason?: string | null;
  user_id?: Uuid | null;
  party_id?: Uuid | null;
  is_deleted_by_user: boolean;
  created_by_user: boolean;
  total_amount: number;
  // Owner spec (2026-06-28): true iff this parcel has an outstanding unpaid
  // debt on the customer ledger. Rendered as a yes/no "debtor" badge on the
  // ERP parcels list and filterable via the `has_debt` query param.
  has_debt: boolean;
  products?: ApiParcelsProductsGetResponse;
  created_at: Iso8601;
  updated_at: Iso8601;
};

export type ApiParcelsListParams = {
  page?: number;
  page_size?: number;
  search?: string;
  status?: ParcelStatus | '';
  party_id?: string | '';
  fulfillment_status?: FulfillmentStatus | '';
  /** CSV of `FulfillmentStatus` values. Used by the Omborxona owned tab to
   *  scope to active workflow states (IN_PROGRESS,SORTING). */
  workflow_status__in?: string;
  /** 'true' → only parcels with a bound user; 'false' → only orphans.
   *  Anything else is treated as "no filter" by the backend. */
  has_owner?: 'true' | 'false';
  /** 'true' → only parcels that currently have unpaid debt; 'false' → only
   *  parcels with no debt. Omit entirely for "all". */
  has_debt?: 'true' | 'false';
  /** 'true' → only parcels currently inside the JP warehouse (workflow in
   *  IN_PROGRESS/SORTING/COLLECTING). Used by the Omborxona owned tab to
   *  drop parcels that have physically left. */
  is_in_warehouse?: 'true' | 'false';
};

export type ApiParcelsListResponse = {
  results: ApiParcelsGetItem[];
  count: number;
  next: string | null;
  previous: string | null;
};

export type ApiParcelsAdminPostBody = {
  cargo_id: string;
  name: string;
  trek_id: string;
  store_name?: string;
  address_name: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  street: string;
  building: string;
  apartment?: string;
  region: Region;
  city: string;
  phone_number: string;
  passport_series: string;
  pinfl: string;
  additional_info?: string;
  // Owner spec (2026-06-01): when true the backend lands the new parcel
  // directly in ``SORTING`` (skipping IN_PROGRESS) — only honored if the
  // trek_id matches an UNCLAIMED OwnerlessProduct already at the warehouse
  // OR ``add_to_warehouse`` is also true (the auto-create path below).
  confirmed_for_shipping?: boolean;
  // Owner spec (2026-06-01): when true the backend also creates an
  // OwnerlessProduct with this trek_id and links the parcel to it, so the
  // box ends up "in the warehouse" without an explicit scanner step.
  add_to_warehouse?: boolean;
};

export type ApiParcelsAdminOwnerlessCheckGetResponse = {
  exists: boolean;
};

export type ApiParcelsAdminPutBody = Partial<ApiParcelsAdminPostBody>;

export type ApiParcelsApprovePostBody = {
  confirm?: boolean;
};

export type ApiParcelsApprovePostResponse = {
  needs_confirmation?: boolean;
  duplicate_count?: number;
  parcel?: ApiParcelsGetItem;
};

export type ApiParcelsRejectPostBody = {
  reason?: string;
};
